/**
 * Analytics Event Type Definitions
 *
 * All tracked events are defined here for type safety and documentation.
 * Events are categorized by their purpose and tracking location (client/server).
 */

import type { MarketPriceScope } from "@/lib/market-prices/types";

// =============================================================================
// Wizard Events (Client-Side)
// =============================================================================

/**
 * Events for tracking wizard/form funnels
 */
export type WizardEvent =
  | "offering_wizard_started"
  | "offering_wizard_step_completed"
  | "offering_wizard_completed"
  | "offering_wizard_abandoned"
  | "request_wizard_started"
  | "request_wizard_step_completed"
  | "request_wizard_completed"
  | "request_wizard_abandoned";

// =============================================================================
// Auth-First Funnel Events (Client-Side) - PLAN-001
// =============================================================================

/**
 * Events for tracking the new auth-first funnel flow.
 * These events provide granular tracking of the user journey through
 * the Schnellanfrage and Schnellangebot funnels.
 */
export type AuthFirstFunnelEvent =
  // Funnel entry
  | "funnel_started"
  | "funnel_step_viewed"
  | "funnel_step_completed"
  | "funnel_completed"
  | "funnel_abandoned"
  // Auth step events
  | "auth_step_started"
  | "auth_method_selected"
  | "auth_completed"
  // Verification events
  | "verification_modal_shown"
  | "verification_field_submitted"
  | "verification_completed"
  | "phone_otp_sent"
  | "phone_otp_verified"
  // Guest OTP (email verification funnel)
  | "guest_otp_requested"
  | "guest_otp_completed"
  | "guest_otp_expired"
  // Guest OTP — send-path instrumentation (added 2026-05-14 with the
  // OtpSendTrigger taxonomy in useEmailOtp.ts; see
  // knowledgebase/observations/OBS-email-confirmation-button-recurring.md).
  | "guest_otp_card_shown"
  | "guest_otp_send_attempted"
  | "guest_otp_send_failed"
  // Verification reminder pipeline (server-side, fired by the cron)
  | "verification_reminder_sent"
  | "verification_reminder_clicked"
  | "verification_expired_notice_sent"
  // Form field interactions
  | "field_focused"
  | "field_filled"
  | "field_error_shown"
  | "field_explanation_viewed";

/**
 * Properties for funnel_started event
 */
export interface FunnelStartedProperties {
  /** "offering" or "request" */
  funnel_type: "offering" | "request";
  /** Entry source (direct, search, referral, etc.) */
  entry_source?: string;
  /** URL parameters present */
  has_product_param?: boolean;
  product_param?: string;
  /** Whether user was already authenticated */
  is_authenticated: boolean;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for funnel_step_viewed and funnel_step_completed events
 */
export interface FunnelStepProperties {
  funnel_type: "offering" | "request";
  /** Current step ID (auth, type, location, details, confirm) */
  step_id: string;
  /** Step index (0-based) */
  step_index: number;
  /** Total steps in funnel */
  total_steps: number;
  /** Time spent on step in milliseconds (for step_completed) */
  time_on_step_ms?: number;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for funnel_completed event
 */
export interface FunnelCompletedProperties {
  funnel_type: "offering" | "request";
  /** Product type selected (okopunkte, ausgleichsflaechen) */
  product_type: string;
  /** Total time from start to completion in milliseconds */
  total_time_ms: number;
  /** Number of offerings/requests created */
  item_count: number;
  /** Whether any geometry was provided */
  has_geometry?: boolean;
  /** Bundesland of first item */
  bundesland?: string;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for funnel_abandoned event
 */
export interface FunnelAbandonedProperties {
  funnel_type: "offering" | "request";
  /** Step where user abandoned */
  abandoned_at_step: string;
  /** Time spent in funnel before abandonment */
  time_in_funnel_ms: number;
  /** Reason for abandonment if known */
  reason?: "navigation" | "timeout" | "error" | "unknown";
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for auth_method_selected event
 */
export interface AuthMethodSelectedProperties {
  funnel_type: "offering" | "request";
  /** Auth method chosen */
  method: "email_otp" | "phone_otp" | "google";
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for auth_completed event
 */
export interface AuthCompletedProperties {
  funnel_type: "offering" | "request";
  /** Auth method used */
  method: "email_otp" | "phone_otp" | "google";
  /** Whether this was a new signup or existing user */
  is_new_user: boolean;
  /** Time from auth start to completion */
  auth_time_ms: number;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for verification events
 */
export interface VerificationProperties {
  funnel_type: "offering" | "request";
  /** What is being verified */
  field: "phone" | "email" | "name";
  /** Whether verification succeeded (for verification_field_submitted) */
  success?: boolean;
  /** Error message if failed */
  error?: string;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for field interaction events
 */
export interface FieldInteractionProperties {
  funnel_type: "offering" | "request";
  /** Step where field interaction occurred */
  step_id: string;
  /** Field identifier */
  field_id: string;
  /** Field type (text, select, radio, etc.) */
  field_type?: string;
  /** Error message shown (for field_error_shown) */
  error_message?: string;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for wizard step events
 */
export interface WizardStepProperties {
  /** Current step index (0-based) */
  step: number;
  /** Human-readable step name */
  step_name: string;
  /** Time spent on previous step in ms */
  time_on_step?: number;
  /** Total steps in the wizard */
  total_steps?: number;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for wizard completion events
 */
export interface WizardCompletedProperties {
  /** Type of offering/request created */
  type: string;
  /** Whether geometry/parcels were provided */
  has_geometry: boolean;
  /** Total time to complete wizard in ms */
  total_time?: number;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

// =============================================================================
// Chatbot Events (Server-Side)
// =============================================================================

/**
 * Chatbot conversion events tracked server-side
 */
export type ChatbotEvent = "chatbot_conversion";

/**
 * Properties for chatbot_conversion event
 */
export interface ChatbotConversionProperties {
  conversion_type: "offering" | "request" | "lead" | "callback";
  conversation_id: string;
  entity_id?: string;
  is_authenticated: boolean;
  current_url?: string;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

// =============================================================================
// Business Events (Server-Side)
// =============================================================================

/**
 * Critical business events tracked server-side for reliability
 */
export type BusinessEvent =
  | "offering_created"
  | "offering_updated"
  | "offering_published"
  | "offering_archived"
  | "request_created"
  | "request_updated"
  | "deal_created"
  | "deal_shared"
  | "deal_status_changed"
  | "deal_completed"
  | "inquiry_sent"
  | "inquiry_responded";

/**
 * Properties for offering_created event
 */
export interface OfferingCreatedProperties {
  offering_id: string;
  /**
   * The distinct axes of the Angebot's rows ("forst", "naturschutz"…): every supply is a row on
   * its axis (ARCH-039 D19). Replaces the product (`type`) an Angebot no longer has.
   */
  axes: string[];
  /** The unit of its Biotopausgleich ("oekopunkte", "ha", "wertpunkte"), or null when it offers none. */
  naturschutz_unit: string | null;
  bundesland: string;
  landkreis?: string;
  area_hectares?: number;
  has_geometry: boolean;
  naturraum_name?: string;
  status: string;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for request_created event
 */
export interface RequestCreatedProperties {
  request_id: string;
  /**
   * The distinct axes of the request's needs ("forst", "naturschutz"…): every need is a line on
   * its axis (ARCH-039 D19). Replaces the product (`type`) a request no longer has.
   */
  axes: string[];
  /** The unit of its Biotopausgleich ("oekopunkte", "ha"), or null when it needs none. */
  naturschutz_unit: string | null;
  bundesland?: string;
  landkreis?: string;
  timing: "under_6_months" | "over_6_months";
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for deal_created event
 */
export interface DealCreatedProperties {
  deal_id: string;
  offering_id: string;
  request_id?: string;
  auto_matched: boolean;
  match_score?: number;
  initial_status: string;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for deal_status_changed event
 */
export interface DealStatusChangedProperties {
  deal_id: string;
  old_status: string;
  new_status: string;
  /** Who triggered the change (system, admin, buyer, seller) */
  changed_by: "system" | "admin" | "buyer" | "seller";
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

// =============================================================================
// User Events (Both Client and Server)
// =============================================================================

/**
 * User lifecycle events
 */
export type UserEvent =
  | "user_signed_up"
  | "user_logged_in"
  | "user_logged_out"
  | "profile_updated"
  | "profile_completed"
  | "email_verified";

/**
 * Properties for user identification
 */
export interface UserProperties {
  email?: string;
  created_at?: string;
  company_type?: string;
  /** User's primary role: buyer, seller, or both */
  role?: "buyer" | "seller" | "both";
  is_verified?: boolean;
  has_offerings?: boolean;
  has_requests?: boolean;
  offering_count?: number;
  request_count?: number;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

// =============================================================================
// Engagement Events (Client-Side)
// =============================================================================

/**
 * User engagement/interaction events
 */
export type EngagementEvent =
  | "map_interaction"
  | "parcel_selected"
  | "contact_modal_opened"
  | "contact_form_submitted"
  | "order_book_viewed"
  | "match_viewed"
  | "offering_detail_viewed"
  | "request_detail_viewed"
  | "search_performed"
  | "filter_applied"
  // Price estimator widget (content page /aktueller-preis-pro-oekopunkt)
  | "price_calculator_viewed"
  | "price_calculator_estimate_shown"
  | "price_calculator_cta_clicked"
  // Procurement-service ("Beschaffungsservice für Unternehmen") link in the
  // price estimator. The user-facing copy deliberately avoids the "ÖPaaS"
  // term, but the event keeps the internal name for continuity with the rest
  // of the ÖPaaS analytics.
  | "price_calculator_opaas_clicked"
  // "Eine Marke der LandVest GmbH" attribution in the site footer — the
  // outbound link to landvest.eu also carries UTM parameters, so the click
  // is attributable on both ends.
  | "landvest_footer_link_clicked"
  // A Flächenanalyse call to action handing off to LandVest from inside the
  // site; `placement` names the surface it sits on.
  | "landvest_cta_clicked"
  // An action in one of the two homepage hero cards. Autocapture is off and
  // there is no navigation event (ARCH-058 D22), so without it the homepage's
  // hand-off into the funnels could only be inferred from pageview pairs.
  | "homepage_hero_cta_clicked";

/**
 * Properties for map_interaction event
 */
export interface MapInteractionProperties {
  /** Type of map action */
  action: "pan" | "zoom" | "draw" | "select_parcel" | "search_address";
  /** Zoom level at time of interaction */
  zoom_level?: number;
  /** Bundesland being viewed */
  bundesland?: string;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for parcel_selected event
 */
export interface ParcelSelectedProperties {
  /** Number of parcels selected */
  parcel_count: number;
  /** Total area in hectares */
  total_area_hectares: number;
  /** Bundesland of parcels */
  bundesland: string;
  /** Resolved Naturraum name */
  naturraum_name?: string;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for order_book_viewed event
 */
export interface OrderBookViewedProperties {
  offering_id: string;
  /** User role viewing the order book */
  viewer_role: "seller" | "buyer";
  /** Number of matching bids visible */
  matching_bid_count: number;
  /** Number of competing asks visible */
  competing_ask_count: number;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * How the visitor resolved their region in the price estimator.
 */
export type PriceCalculatorInputMethod = "plz" | "region_select";

/**
 * Properties for price_calculator_viewed event.
 * Fired once per mount of the estimator section.
 */
export interface PriceCalculatorViewedProperties {
  /** Slug of the content page hosting the widget */
  page: "aktueller-preis-pro-oekopunkt";
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for price_calculator_estimate_shown event.
 * Fired (debounced) whenever a new estimate summary renders.
 */
export interface PriceCalculatorEstimateShownProperties {
  /** Postal code, only present when resolved via PLZ input */
  plz?: string;
  bundesland: string;
  naturraum: string;
  /** Naturraum D-code (e.g. "D34"), omitted when unknown */
  naturraum_zone?: string;
  /** Requested Ökopunkte quantity */
  quantity: number;
  /** Which scope answered the market-prices cascade */
  scope: MarketPriceScope;
  /** Number of available ask-side offerings in scope */
  ask_count: number;
  /** Scoped ask price range (null below the privacy threshold) */
  ask_min: number | null;
  ask_max: number | null;
  /** How the region was resolved */
  input_method: PriceCalculatorInputMethod;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for price_calculator_cta_clicked event.
 * Fired (instantly) right before navigating into the Schnellanfrage funnel.
 */
export interface PriceCalculatorCtaClickedProperties {
  plz?: string;
  bundesland: string;
  naturraum: string;
  /** Naturraum D-code (e.g. "D34"), omitted when unknown */
  naturraum_zone?: string;
  quantity: number;
  scope: MarketPriceScope;
  ask_count: number;
  ask_min: number | null;
  ask_max: number | null;
  input_method: PriceCalculatorInputMethod;
  /** Funnel the CTA routes to */
  destination: "schnellanfrage";
  /** The exact button label the visitor clicked (e.g. "Passende Angebote anfordern") */
  cta_label: string;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for price_calculator_opaas_clicked event.
 * Fired (instantly) right before navigating to the procurement-service page
 * via the "Beschaffungsservice für Unternehmen" link. Mirrors the cta_clicked
 * context, but every field is optional because the link is also offered in the
 * below-threshold state where parts of the estimate context may be absent.
 */
export interface PriceCalculatorOpaasClickedProperties {
  plz?: string;
  bundesland?: string;
  naturraum?: string;
  /** Naturraum D-code (e.g. "D34"), omitted when unknown */
  naturraum_zone?: string;
  quantity?: number;
  scope?: MarketPriceScope;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Properties for homepage_hero_cta_clicked event.
 * Fired (instantly) when a visitor follows an action in a homepage hero card.
 */
export interface HomepageHeroCtaClickedProperties {
  /** The card's side of the market: buyer ("Kompensation finden") or seller */
  card: "buyer" | "seller";
  /** The direct action, the way in for the undecided, or the link under the figures */
  action: "primary" | "secondary" | "more";
  /** The route the action links, before any LandVest hand-off resolution */
  destination: string;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

// =============================================================================
// All Events Union Type
// =============================================================================

/**
 * Union of all possible event names
 */
/** Campaign interaction diagnostics. Never include free text, credentials, or field values. */
export type SellerRefreshEvent =
  | "seller_refresh_load_failed"
  | "seller_refresh_retry_clicked"
  | "seller_refresh_offering_viewed"
  | "seller_refresh_offering_selected"
  | "seller_refresh_field_changed"
  | "seller_refresh_edit_opened"
  | "seller_refresh_save_attempted"
  | "seller_refresh_validation_failed"
  | "seller_refresh_help_clicked"
  | "seller_refresh_withdraw_clicked"
  | "seller_refresh_save_succeeded"
  | "seller_refresh_save_failed"
  | "seller_refresh_guide_opened"
  | "seller_refresh_edits_discarded"
  | "seller_refresh_field_help_opened"
  | "seller_refresh_new_offering_clicked"
  | "seller_refresh_overview_clicked";

export type AnalyticsEvent =
  | SellerRefreshEvent
  | WizardEvent
  | AuthFirstFunnelEvent
  | ChatbotEvent
  | BusinessEvent
  | UserEvent
  | EngagementEvent;

// =============================================================================
// Event Categories (for filtering)
// =============================================================================

/**
 * Event category enum for PostHog filtering
 */
export const EventCategory = {
  WIZARD: "wizard",
  FUNNEL: "funnel",
  BUSINESS: "business",
  USER: "user",
  CHATBOT: "chatbot",
  ENGAGEMENT: "engagement",
} as const;

export type EventCategory = (typeof EventCategory)[keyof typeof EventCategory];

/**
 * Map events to their categories
 */
export const eventCategoryMap: Record<AnalyticsEvent, EventCategory> = {
  seller_refresh_load_failed: EventCategory.FUNNEL,
  seller_refresh_retry_clicked: EventCategory.FUNNEL,
  seller_refresh_offering_viewed: EventCategory.FUNNEL,
  seller_refresh_offering_selected: EventCategory.FUNNEL,
  seller_refresh_field_changed: EventCategory.FUNNEL,
  seller_refresh_edit_opened: EventCategory.FUNNEL,
  seller_refresh_save_attempted: EventCategory.FUNNEL,
  seller_refresh_validation_failed: EventCategory.FUNNEL,
  seller_refresh_help_clicked: EventCategory.FUNNEL,
  seller_refresh_withdraw_clicked: EventCategory.FUNNEL,
  seller_refresh_save_succeeded: EventCategory.FUNNEL,
  seller_refresh_save_failed: EventCategory.FUNNEL,
  seller_refresh_guide_opened: EventCategory.FUNNEL,
  seller_refresh_edits_discarded: EventCategory.FUNNEL,
  seller_refresh_field_help_opened: EventCategory.FUNNEL,
  seller_refresh_new_offering_clicked: EventCategory.FUNNEL,
  seller_refresh_overview_clicked: EventCategory.FUNNEL,

  // Wizard events (legacy)
  offering_wizard_started: EventCategory.WIZARD,
  offering_wizard_step_completed: EventCategory.WIZARD,
  offering_wizard_completed: EventCategory.WIZARD,
  offering_wizard_abandoned: EventCategory.WIZARD,
  request_wizard_started: EventCategory.WIZARD,
  request_wizard_step_completed: EventCategory.WIZARD,
  request_wizard_completed: EventCategory.WIZARD,
  request_wizard_abandoned: EventCategory.WIZARD,

  // Auth-first funnel events (PLAN-001)
  funnel_started: EventCategory.FUNNEL,
  funnel_step_viewed: EventCategory.FUNNEL,
  funnel_step_completed: EventCategory.FUNNEL,
  funnel_completed: EventCategory.FUNNEL,
  funnel_abandoned: EventCategory.FUNNEL,
  auth_step_started: EventCategory.FUNNEL,
  auth_method_selected: EventCategory.FUNNEL,
  auth_completed: EventCategory.FUNNEL,
  verification_modal_shown: EventCategory.FUNNEL,
  verification_field_submitted: EventCategory.FUNNEL,
  verification_completed: EventCategory.FUNNEL,
  phone_otp_sent: EventCategory.FUNNEL,
  phone_otp_verified: EventCategory.FUNNEL,
  guest_otp_requested: EventCategory.FUNNEL,
  guest_otp_completed: EventCategory.FUNNEL,
  guest_otp_expired: EventCategory.FUNNEL,
  guest_otp_card_shown: EventCategory.FUNNEL,
  guest_otp_send_attempted: EventCategory.FUNNEL,
  guest_otp_send_failed: EventCategory.FUNNEL,
  verification_reminder_sent: EventCategory.FUNNEL,
  verification_reminder_clicked: EventCategory.FUNNEL,
  verification_expired_notice_sent: EventCategory.FUNNEL,
  field_focused: EventCategory.FUNNEL,
  field_filled: EventCategory.FUNNEL,
  field_error_shown: EventCategory.FUNNEL,
  field_explanation_viewed: EventCategory.FUNNEL,

  // Chatbot events
  chatbot_conversion: EventCategory.CHATBOT,

  // Business events
  offering_created: EventCategory.BUSINESS,
  offering_updated: EventCategory.BUSINESS,
  offering_published: EventCategory.BUSINESS,
  offering_archived: EventCategory.BUSINESS,
  request_created: EventCategory.BUSINESS,
  request_updated: EventCategory.BUSINESS,
  deal_created: EventCategory.BUSINESS,
  deal_shared: EventCategory.BUSINESS,
  deal_status_changed: EventCategory.BUSINESS,
  deal_completed: EventCategory.BUSINESS,
  inquiry_sent: EventCategory.BUSINESS,
  inquiry_responded: EventCategory.BUSINESS,

  // User events
  user_signed_up: EventCategory.USER,
  user_logged_in: EventCategory.USER,
  user_logged_out: EventCategory.USER,
  profile_updated: EventCategory.USER,
  profile_completed: EventCategory.USER,
  email_verified: EventCategory.USER,

  // Engagement events
  map_interaction: EventCategory.ENGAGEMENT,
  parcel_selected: EventCategory.ENGAGEMENT,
  contact_modal_opened: EventCategory.ENGAGEMENT,
  contact_form_submitted: EventCategory.ENGAGEMENT,
  order_book_viewed: EventCategory.ENGAGEMENT,
  match_viewed: EventCategory.ENGAGEMENT,
  offering_detail_viewed: EventCategory.ENGAGEMENT,
  request_detail_viewed: EventCategory.ENGAGEMENT,
  search_performed: EventCategory.ENGAGEMENT,
  filter_applied: EventCategory.ENGAGEMENT,
  price_calculator_viewed: EventCategory.ENGAGEMENT,
  price_calculator_estimate_shown: EventCategory.ENGAGEMENT,
  price_calculator_cta_clicked: EventCategory.ENGAGEMENT,
  price_calculator_opaas_clicked: EventCategory.ENGAGEMENT,
  landvest_footer_link_clicked: EventCategory.ENGAGEMENT,
  landvest_cta_clicked: EventCategory.ENGAGEMENT,
  homepage_hero_cta_clicked: EventCategory.ENGAGEMENT,
};
